import { PropsWithChildren, useCallback, useEffect, useState } from 'react'
import { Outlet } from 'react-router-dom'
import { AssetImage } from '@/shared/ui/Asset'
import { cn } from '@lair-finance/util'
import { useEffectOnce, useInterval } from 'react-use'
import { range } from 'lodash-es'

export const LoadingLayout = ({ children }: PropsWithChildren) => {
  const [loading, setLoading] = useState(true)
  const complete = () => setLoading(false)

  if (loading) {
    return (
      <div className={'flex-center flex-col h-screen bg-[#6E61FF] transform tablet:scale-[1.2] -translate-y-[32px]'}>
        <AssetImage filename={'logo.png'} directory={'game'} width={120} height={36} className={'mb-6'} />
        <ProgressBar onCompleted={complete} />
      </div>
    )
  }

  return children || <Outlet />
}

function ProgressBar({ className, onCompleted }: { className?: string; onCompleted?: () => void }) {
  const MAX_VALUE = 21
  const [value, setValue] = useState(0)

  const tick = useCallback(() => {
    setValue(prev => {
      const nextValue = prev + Math.random() * 5
      return nextValue >= MAX_VALUE ? MAX_VALUE : nextValue
    })
  }, [])

  useEffectOnce(tick)
  useInterval(tick, 100)

  useEffect(() => {
    if (value >= MAX_VALUE) {
      onCompleted?.()
    }
  }, [value, onCompleted])

  return (
    <div
      className={cn(
        'relative inline-flex max-w-[195px] w-full h-[20px] px-[6px] py-[4px]',
        'before:pointer-events-none before:absolute before:top-0 before:left-[4px] before:w-[calc(100%-8px)] before:h-[2px] before:bg-[#FAFAFF]',
        'after:pointer-events-none after:absolute after:bottom-0 after:left-[4px] after:w-[calc(100%-8px)] after:h-[2px] after:bg-[#FAFAFF]',
        className,
      )}
    >
      <div
        className={cn(
          'before:pointer-events-none before:absolute before:top-[2px] before:left-[2px] before:size-[2px] before:bg-[#FAFAFF]',
          'after:pointer-events-none after:absolute after:top-[2px] after:right-[2px] after:size-[2px] after:bg-[#FAFAFF]',
        )}
      />
      <div
        className={cn(
          'before:pointer-events-none before:absolute before:bottom-[2px] before:left-[2px] before:size-[2px] before:bg-[#FAFAFF]',
          'after:pointer-events-none after:absolute after:bottom-[2px] after:right-[2px] after:size-[2px] after:bg-[#FAFAFF]',
        )}
      />
      <div
        className={cn(
          'before:pointer-events-none before:absolute before:top-[4px] before:left-0 before:w-[2px] before:h-[calc(100%-8px)] before:bg-[#FAFAFF]',
          'after:pointer-events-none after:absolute after:top-[4px] after:right-0 after:w-[2px] after:h-[calc(100%-8px)] after:bg-[#FAFAFF]',
        )}
      />
      <div className={'grid grid-cols-[repeat(21,minmax(0,1fr))] gap-x-px w-full'}>
        {range(0, Math.floor(value)).map(key => (
          <div className={'inline-flex bg-[#FAFAFF]'} key={key} />
        ))}
      </div>
    </div>
  )
}
